import { useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { ColorName, List, ListItem } from 'types';

import { useAppSelector } from 'redux/hooks';

import { routes } from 'data/routes';

import { ButtonMotives } from 'components/Button';
import { SwitchBtn } from 'components/common/SwitchBtn/SwitchBtn';

import * as S from './Dropdown.styles';
import { SubDropdown } from './components/SubDropdown';

type SubTriggerItemProps = {
  label: string;
  iconLeft?: ListItem['iconLeft'];
  iconRight?: ListItem['iconRight'];
  to?: string;
  onClick?: () => void;
  subItems?: ListItem['subItems'];
  hasSwitchButton?: boolean;
  isOn?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  itemMotive: ButtonMotives;
  barColor: ColorName;
  isCentered: boolean;
};

const SubTriggerItem = ({
  label,
  iconLeft,
  iconRight,
  to,
  onClick,
  subItems,
  hasSwitchButton,
  isOn,
  onChange,
  disabled,
  itemMotive,
  barColor,
  isCentered,
}: SubTriggerItemProps) => {
  const [isSubOpen, isSubOpenSet] = useState(false);
  const subTriggerRef = useRef<HTMLDivElement>(null);

  const handleSubClick = () => {
    if (subItems) {
      isSubOpenSet((prev) => !prev);
    }
  };

  return (
    <S.SubTrigger ref={subTriggerRef}>
      {disabled ? (
        <S.DisabledItem>{label}</S.DisabledItem>
      ) : (
        <S.ListItemButton
          iconLeft={iconLeft}
          iconRight={iconRight}
          motive={itemMotive}
          asLink={to !== 'undefined'}
          to={to}
          scale="xs"
          onClick={subItems ? handleSubClick : onClick}
          fontWeight="extraLight"
          fontFamily="LFT Zeno"
          whiteSpace="normal"
          barColor={barColor}
          $compact={itemMotive === 'settings'}
          $centered={isCentered}
          textAlign={isCentered ? 'center' : undefined}
          lineHeight={itemMotive === 'settings' ? '1.3' : undefined}
          isFlexStretch={hasSwitchButton}
        >
          {hasSwitchButton && isOn !== undefined && onChange ? (
            <SwitchBtn label={label} onChange={onChange} isOn={isOn} />
          ) : (
            label
          )}
        </S.ListItemButton>
      )}

      {subItems ? (
        <S.SubDropdown isOpen={isSubOpen}>
          <SubDropdown items={subItems} itemMotive={itemMotive} />
        </S.SubDropdown>
      ) : undefined}
    </S.SubTrigger>
  );
};

const useDropdown = (
  list: List[],
  itemMotive: ButtonMotives = 'default',
  hasAnimation: boolean = false,
  itemsHasBorder: boolean = false,
  alignLeft: boolean = false
) => {
  const [isOpen, isOpenSet] = useState(false);

  const { barColor } = useAppSelector((state) => state.settings);

  const isHover = useMedia('(hover:hover)');

  const isCentered = itemMotive === 'settings' && !alignLeft;

  const listMapped = list?.map((item: List) => {
    const isOnlyOneListItem =
      item?.items && item.items.length === 1 && item.title === item.items[0].label;

    const titleComponent = isOnlyOneListItem ? (
      <S.ListItemButton
        motive={itemMotive}
        asLink
        to={`${routes.catalogue}/${item.title
          ?.replace(' ', '-')
          .toLocaleLowerCase()}/${item.title?.replace(' ', '-').toLocaleLowerCase()}`}
        fontWeight="extraLight"
        fontFamily="LFT Zeno"
        whiteSpace="normal"
        barColor={barColor as ColorName}
        $compact={itemMotive === 'settings'}
        $centered={isCentered}
        textAlign={isCentered ? 'center' : undefined}
        lineHeight={itemMotive === 'settings' ? '1.3' : undefined}
      >
        {item.title}
      </S.ListItemButton>
    ) : (
      <S.Title textTransform="uppercase" color="title">
        {item?.title}
      </S.Title>
    );

    return (
      <S.Wrapper key={item.id} $compact={itemMotive === 'settings'}>
        {item?.title ? titleComponent : null}

        {!isOnlyOneListItem ? (
          <S.List data-testid="list">
            {item.items.map(
              ({
                label,
                iconLeft,
                iconRight,
                to,
                onClick,
                subItems,
                hasSwitchButton,
                isOn,
                onChange,
                disabled,
              }: ListItem) => (
                <S.ListItem
                  hasAnimation={hasAnimation}
                  itemsHasBorder={itemsHasBorder}
                  key={label}
                >
                  {label && (
                    <SubTriggerItem
                      label={label}
                      iconLeft={iconLeft}
                      iconRight={iconRight}
                      to={to}
                      onClick={onClick}
                      subItems={subItems}
                      hasSwitchButton={hasSwitchButton}
                      isOn={isOn}
                      onChange={onChange}
                      disabled={disabled}
                      itemMotive={itemMotive}
                      barColor={barColor as ColorName}
                      isCentered={isCentered}
                    />
                  )}
                </S.ListItem>
              )
            )}
          </S.List>
        ) : null}
      </S.Wrapper>
    );
  });

  return { listMapped, isOpen, isOpenSet, isHover, barColor };
};

export { useDropdown };
